import { useState } from 'react'

import { Alert, Box, Link, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { AtsJobApplicationFilesOfferLetterDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { SendOfferLetterModal } from 'modules/ats/components/SendOfferLetterModal'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

import { OfferLetterItem } from './OfferLetterItem'

interface Props {
  applicationId: number
  jobPostingId: number
}

export const OfferLetter = ({ applicationId, jobPostingId }: Props) => {
  const displayUpgradeMessage = !hasFeature('ats_upload_files') && !hasFeature('ats_offer_letter')
  const [upgradeModal, setUpgradeModal] = useState(false)
  const [isSendOfferLetterModalOpen, setSendOfferLetterModalOpen] = useState(false)

  const { data } = useQuery(AtsJobApplicationFilesOfferLetterDocument, {
    variables: { applicationId, jobPostingId },
  })

  const offerLetterData = data?.ats.offerLetters[0]
  const applicationData = data?.ats.application

  if (!applicationData) {
    return null
  }

  return (
    <Box>
      {displayUpgradeMessage && (
        <Alert color="warning">
          <Text.Normal>
            {i18n.tView('ats.candidates.files.upgrade_message', {
              upgrade_plan_link: (
                <Link.Touchable
                  onClick={() => {
                    setUpgradeModal(true)
                  }}
                >
                  {i18n.t('ats.candidates.files.upgrade_link_label')}
                </Link.Touchable>
              ),
            })}
          </Text.Normal>
        </Alert>
      )}

      {offerLetterData ? (
        <Box gap="s24">
          <Text.Normal color="textPrimary" weight="semibold">
            {i18n.t('ats.candidates.files.offer_letter.header')}
          </Text.Normal>
          <OfferLetterItem
            offerLetter={offerLetterData}
            applicationId={applicationId}
            jobPostingId={jobPostingId}
          />
        </Box>
      ) : (
        <Box
          flexDirection="row"
          gap="s12"
          paddingX="s16"
          paddingY="s12"
          justifyContent="spaceBetween"
          width="full"
          alignItems="center"
          borderRadius={{ all: 'abs016' }}
          border={{
            all: {
              width: 's1',
              style: 'solid',
              color: 'grey400',
            },
          }}
        >
          <Box gap="s2">
            <Text.Normal size="200" color="textPrimary" weight="medium">
              {i18n.t('ats.candidates.files.offer_letter.header')}
            </Text.Normal>
            <Text.Normal size="200">
              {i18n.t('ats.candidates.files.offer_letter.description')}
            </Text.Normal>
          </Box>
          <F0Button
            label={i18n.t('ats.offer_letter.send_offer_letter')}
            onClick={() => setSendOfferLetterModalOpen(true)}
          />
        </Box>
      )}

      {upgradeModal && (
        <RequestUpgradeModal
          requestReason={ValidReasons.UpgradeFromLegacy}
          requestSource="ats-offer-letter"
          onClose={() => {
            setUpgradeModal(false)
          }}
        />
      )}

      {isSendOfferLetterModalOpen && (
        <SendOfferLetterModal
          application={applicationData}
          onClose={() => setSendOfferLetterModalOpen(false)}
        />
      )}
    </Box>
  )
}
